/*
  ============================================================
  FILE: ctx-cart-add.js
  LOCATION: assets/ctx-cart-add.js
  LOADED: layout/theme.liquid — SITE-WIDE, on purpose.

  Makes every Add to Cart form add in the background and open the cart drawer,
  instead of posting the form and navigating to /cart.

  WHY THIS EXISTS

  Dawn does this with a <product-form> custom element wrapping the form, and
  product-form.js is loaded on every product page — but this theme's forms have
  no <product-form> around them. custom-product.liquid builds its own
  {% form 'product' %}, and so does every product card on a collection page
  (29 of them on a single grid). The element never upgrades, nothing listens for
  the submit, and the browser does what browsers do with a form: posts it and
  navigates. The item DID land in the cart, so this never looked like a server
  problem; it just threw the customer off the page they were on with the drawer
  never opening.

  Site-wide rather than per-section because the same bare form appears on
  collection pages, search results and anywhere a product card renders — binding
  it in one place is what stops the next new card grid shipping broken.

  The drawer handling below is deliberately the same sequence the PDP bundler
  and you-may-be-interested-in already use, because on this theme opening the
  drawer alone is not enough — see the comment in refreshAndOpen.
  ============================================================
*/

(function () {
  'use strict';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function reveal(drawer) {
    /* is-empty is baked in at render time. If the page loaded with an empty
       cart the drawer still carries it, and the panel slides in announcing an
       empty cart while the item sits inside — which reads as a failed add. */
    drawer.classList.remove('is-empty');
    var overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (overlay && typeof drawer.close === 'function') {
      overlay.addEventListener('click', drawer.close.bind(drawer));
    }
    drawer.open();
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  function refreshAndOpen() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;

    /* Re-fetch the drawer section and swap its innards in. Deliberately NOT
       drawer.renderContents(): that path pipes section HTML through a lookup
       with no null check and throws on this header, which has no
       cart-icon-bubble section. */
    fetch(root() + '?section_id=cart-drawer')
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (html) {
          var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#CartDrawer');
          var live = document.querySelector('#CartDrawer');
          if (fresh && live) live.innerHTML = fresh.innerHTML;
        }
        reveal(drawer);
      })
      .catch(function () {
        /* The item is in the cart; only the redraw failed. Still show it. */
        reveal(drawer);
      });
  }

  /* Exposed so the sections that do their own cart/add.js POST — the bundler,
     you-may-be-interested-in, and the Care+ / add-ons box — can share one
     implementation instead of each carrying a copy. The Care+ box did not have
     one: it called drawer.open() straight after adding, which opens the markup
     the page was rendered with. Add only an extended warranty and the drawer
     slid in showing the cart as it was BEFORE the add, so the badge said 2 and
     the drawer showed 1. */
  window.ctxRefreshAndOpenCart = refreshAndOpen;

  function showError(form, message) {
    var box = form.querySelector('[data-ctx-atc-error]');
    if (!box) {
      box = document.createElement('p');
      box.setAttribute('data-ctx-atc-error', '');
      box.setAttribute('role', 'alert');
      box.style.cssText = 'margin:10px 0 0;font-size:13.5px;line-height:1.4;color:#B42318;';
      form.appendChild(box);
    }
    box.textContent = message;
    box.hidden = false;
  }

  function bind(form) {
    if (form.dataset.ctxAtcBound === '1') return;

    /* Stand aside only when Dawn's element is genuinely in charge — which means
       checking the custom-element REGISTRY, not the presence of the tag.

       Product cards render a <product-form> wrapper, but product-form.js is
       only loaded on the product page. So on a collection page there are 29
       <product-form> elements with nothing behind them: never upgraded, no
       listener, and the browser posts the form. Skipping on the tag alone meant
       standing aside for an empty shell, which is exactly how every card on
       every collection page kept navigating to /cart. */
    if (form.closest('product-form') && customElements.get('product-form')) return;
    form.dataset.ctxAtcBound = '1';

    form.addEventListener('submit', function (e) {
      /* Only the Add to Cart button. The dynamic checkout buttons live in this
         same form and are driven by Shopify's own script — intercepting those
         would break Shop Pay. */
      if (e.submitter && e.submitter.getAttribute('name') !== 'add') return;

      var drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return;  // no drawer: post normally

      e.preventDefault();

      var btn = form.querySelector('[name="add"]') || form.querySelector('button[type="submit"]');
      var label = btn ? btn.innerHTML : '';
      if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }

      var err = form.querySelector('[data-ctx-atc-error]');
      if (err) err.hidden = true;

      fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      })
        .then(function (res) {
          return res.json().then(function (data) { return { ok: res.ok, data: data }; });
        })
        .then(function (r) {
          if (!r.ok) {
            throw new Error(r.data.description || r.data.message || 'Could not add this to your cart.');
          }
          refreshAndOpen();
        })
        .catch(function (error) {
          showError(form, error.message || 'Could not add this to your cart.');
        })
        .then(function () {
          if (btn) {
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
            btn.innerHTML = label;
          }
        });
    });
  }

  function bindAll() {
    document.querySelectorAll('form[action*="/cart/add"]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll);
  } else {
    bindAll();
  }

  /* The theme editor re-renders the section on every setting change, which
     replaces the form element and drops the listener with it. */
  document.addEventListener('shopify:section:load', bindAll);

  /* ── CONTINUE SHOPPING ──────────────────────────────────────────────────
     Sends the customer back to what they were actually looking at.

     The empty drawer's button is hard-coded to /collections/all, so emptying
     your cart while reading a product page dumped you at Shop All and you lost
     your place. Two cases:

       on a product page — you are already there, so just close the drawer
       anywhere else     — return to the last product page visited this session

     Delegated from the document rather than bound to the button, because the
     drawer's markup is replaced wholesale every time the cart changes and any
     listener attached directly to the link dies with it. Falls through to the
     original link when there is no history to go back to. */
  var LAST_PRODUCT_KEY = 'ctx:last-product';

  try {
    if (location.pathname.indexOf('/products/') !== -1) {
      sessionStorage.setItem(LAST_PRODUCT_KEY, location.pathname + location.search);
    }
  } catch (e) { /* private mode — the fallback below still works */ }

  document.addEventListener('click', function (e) {
    var link = e.target && e.target.closest ? e.target.closest('a') : null;
    if (!link) return;

    var href = link.getAttribute('href') || '';
    if (!/\/collections\/all\/?$/.test(href)) return;

    /* Only the continue-shopping button, not a genuine Shop All link in the nav. */
    if (!link.closest('cart-drawer') && !link.closest('.drawer__inner-empty, .cart__empty-text, .cart__warnings')) return;

    if (location.pathname.indexOf('/products/') !== -1) {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.close === 'function') {
        e.preventDefault();
        drawer.close();
      }
      return;
    }

    var last = null;
    try { last = sessionStorage.getItem(LAST_PRODUCT_KEY); } catch (err) {}
    if (last) {
      e.preventDefault();
      window.location.href = last;
    }
  });
})();
